"use client";
import Comments from "@/components/comments/comments";
import RatingSeries from "@/components/item-info/rating-series/rating-series";
import Rating from "@/components/item-info/rating/rating";
import ShowScore from "@/components/item-info/show-score/show-score";
import { useAuth } from "@/context/auth-context";
import { useApi } from "@/hooks/useApi";
import { getItem } from "@/lib/api/item/info";
import { BProgress } from "@bprogress/core";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { BsFillXSquareFill } from "react-icons/bs";
import { FiExternalLink } from "react-icons/fi";
import { IoOpenOutline, IoTrashBin } from "react-icons/io5";
import useSWR from "swr";
import { useToast } from "../../hooks/useToast";
import ThreeColumnsAds from "../ads/three-columns-ads";
import TwoColumnsAds from "../ads/two-columns-ads";
import { AdCode } from "../enums/ad-code";
import { MovieSeriesStatusOptions } from "../enums/movie-series-status";
import RecentClips from "../recent-clips/recent-clips";
import {
	createTrailer,
	getLabelFromEnumOptions,
	minutesToPersian,
} from "../util/functions";
import ItemAwards from "./item-awards/item-awards";
import NewsContentSlider from "./news-content/news-content-slider";
import RemoveScoreModal from "./remove-score/remove-score-modal";
import Tags from "./tags/tags";
import { useTrackVisit } from "@/hooks/useTrackVisit";

export default function ItemInfo({
	initialItem,
	kind,
	news,
	ads,
	clips,
}: {
	initialItem: any;
	kind: any;
	news: any[];
	ads: any[];
	clips: any;
}) {
	const { user } = useAuth();
	const { addToast } = useToast();
	useTrackVisit("CONTENT", initialItem?.id);
	const { call: callMarked, loading: markedLoading } = useApi();
	const { call: callUserScore, loading: userScoreLoading } = useApi();
	const { call: callEpisodeScores, loading: episodeScoreLoading } = useApi();
	const loading = markedLoading || userScoreLoading || episodeScoreLoading;
	const contentRef = useRef<any>(null);
	const backgroundRef = useRef<any>(null);
	const [scoreSeason, setScoreSeason] = useState<null | {
		episode: number;
		season: number;
		score: number;
	}>(null);
	const [newsActiveColor, setNewsActiveColor] = useState<string>("");
	const [detailedScore, setDetailedScore] = useState(false);
	const [showScore, setShowScore] = useState(false);

	const secondRowAds =
		ads?.filter(
			(ad) => ad.code === AdCode.CODE_2 || ad.code === AdCode.CODE_3,
		) || [];

	const thirdRowAds =
		ads?.filter(
			(ad) =>
				ad.code === AdCode.CODE_4 ||
				ad.code === AdCode.CODE_5 ||
				ad.code === AdCode.CODE_6,
		) || [];

	const updateBackgroundHeight = () => {
		if (contentRef.current && backgroundRef.current) {
			const contentHeight = contentRef.current?.offsetHeight;
			backgroundRef.current.style.height = `${contentHeight}px`;
		}
	};

	const {
		data: item,
		mutate,
		isLoading: isItemLoading,
	} = useSWR(
		["item", kind?.kind, initialItem?.id],
		() => getItem(kind?.kind, initialItem?.id),
		{ fallbackData: initialItem, revalidateOnMount: false },
	);

	const [rating, setRating] = useState<number | undefined>(undefined);
	const [scored, setScored] = useState<boolean>(false);
	const [marked, setMarked] = useState(true);
	const [removeScoreModal, setRemoveScoreModal] = useState(false);
	const [episodeScores, setEpisodeScores] = useState<any[]>([]);

	useEffect(() => {
		// Initial height adjustment
		updateBackgroundHeight();

		// Update height on window resize
		window.addEventListener("resize", updateBackgroundHeight);

		// Clean up
		return () => {
			window.removeEventListener("resize", updateBackgroundHeight);
		};
	}, []);

	useEffect(() => {
		if (loading || isItemLoading) {
			BProgress.start();
		} else {
			BProgress.done();
		}
	}, [loading, isItemLoading]);

	const fetchUserScores = async () => {
		try {
			const res = callEpisodeScores(
				`/api/score/episode?contentId=${item.id}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
					},
					cache: "no-store",
				},
				false,
			);
			const data = await res;
			setEpisodeScores(data);
			data && data.length > 0 && setDetailedScore(true);
		} catch (error) {
			console.error("Failed to fetch user marked:", error);
		}
	};

	const fetchUserScore = async () => {
		try {
			const res = await callUserScore(
				`/api/score?contentId=${item.id}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
					},
				},
				false,
			);
			const data = res;
			setRating(data?.scored ? data.point : 0);
			setScored(data?.scored);
		} catch (error) {
			console.error("Failed to fetch user score:", error);
			setRating(0);
		}
	};

	useEffect(() => {
		const fetchMarked = async () => {
			try {
				const res = callMarked(
					`/api/mark-as-done?contentId=${item.id}`,
					{
						method: "GET",
						headers: {
							"Content-Type": "application/json",
						},
						cache: "no-store",
					},
					false,
				);
				const data = await res;
				setMarked(data?.marked ?? true);
			} catch (error) {
				console.error("Failed to fetch user marked:", error);
				setMarked(true);
			}
		};

		if (item.id && user) {
			fetchMarked();
			fetchUserScores();
			fetchUserScore();
		}
	}, [item.id, user]);

	const trailer = useMemo(() => {
		return createTrailer(item, kind, clips.trailer);
	}, [item, kind, clips.trailer]);

	return (
		<>
			<div
				ref={backgroundRef}
				className="absolute -z-10 w-full top-[-64px]"
				style={{
					backgroundImage: `url(${
						process.env.NEXT_PUBLIC_FILE_URL + item?.bannerImage?.code
					}),
                      linear-gradient(to bottom, transparent 80%, rgba(0, 0, 0, 1)),
                      linear-gradient(to right, transparent 30%, rgba(0, 0, 0, 1)),
                      linear-gradient(to left, transparent 30%, rgba(0, 0, 0, 1))
                    `,
					backgroundRepeat: "no-repeat",
					backgroundSize: "cover",
					backgroundPosition: "top",
					backgroundBlendMode: "overlay",
					filter: "blur(16px)",
				}}
			></div>
			<div className="text-white py-6 lg:px-[30px] xl:px-[80px] flex flex-col items-start">
				<div
					ref={contentRef}
					className="w-full py-6 rounded-lg shadow-lg flex flex-row justify-between gap-4 md:gap-16"
				>
					{/* Movie Details */}
					<div className="w-3/5 lg:w-2/3 py-6 flex flex-col gap-4 px-4">
						<h1 className="text-[20px] sm:text-[24px] md:text-[30px] lg:text-[48px] font-extraBold">
							{item?.title}
						</h1>
						<div>
							<h2 className="text-[14px] sm:text-[18px] md:text-[24px] text-white font-semiBold whitespace-nowrap">
								خلاصه داستان
							</h2>
							<p className="font-light text-[10px] sm:text-[12px] md:text-[16px] text-white line-clamp-2 overflow-hidden">
								{item?.description}
							</p>
						</div>
						<div className="mt-4 lg:grid hidden lg:grid-cols-3 gap-4">
							{item.seasons && item.seasons.length > 0 && (
								<div className="flex items-center gap-1.5">
									<p className="font-light text-sm md:text-base whitespace-nowrap">
										تعداد فصل
									</p>
									<p className="font-semibold text-sm md:text-lg">
										{item.seasons?.length}
									</p>
								</div>
							)}
							<div className="flex items-center gap-1.5">
								<p className="font-light text-sm md:text-base whitespace-nowrap">
									تعداد جوایز
								</p>
								<p className="font-semibold text-sm md:text-lg">
									{item.awards?.length ?? 0}
								</p>
							</div>
							{item.duration && (
								<div className="flex items-center gap-1.5">
									<p className="font-light text-sm md:text-base">
										مدت زمان هر قسمت
									</p>
									<p className="font-semibold text-sm md:text-lg">
										{minutesToPersian(item?.duration)}
									</p>
								</div>
							)}

							{item.genres && item.genres.length > 0 && (
								<div className="flex items-center gap-1.5">
									<p className="font-light text-sm md:text-base whitespace-nowrap">
										ژانرها
									</p>
									<div className="flex flex-wrap font-semibold text-sm md:text-lg">
										{item?.genres
											.slice(0, 3)
											.map((genre: any, index: number) => (
												<Link
													href={`/${kind?.kind}/genres/${genre.id}`}
													key={index}
													className="hover:opacity-70 pl-1 whitespace-nowrap"
												>
													{genre.persianName}
													{index !== Math.min(item?.genres.length - 1, 2) &&
														"،"}
												</Link>
											))}
									</div>
								</div>
							)}

							{item.directors && item.directors.length > 0 && (
								<div className="flex items-center gap-1.5">
									<p className="font-light text-sm md:text-base whitespace-nowrap">
										کارگردان
									</p>
									<p className="font-semibold text-sm md:text-lg">
										{item?.directors
											?.slice(0, 3)
											.map((director: any, index: number) => {
												const isLast =
													index ===
													Math.min(item?.directors?.length ?? 0, 3) - 1;
												return (
													<span
														key={director.id ?? index}
														className="inline-flex items-center"
													>
														<Link
															href={`/characters/${director.id}`}
															className="hover:opacity-70 whitespace-nowrap"
														>
															{director.name}
														</Link>
														{!isLast && <span className="pl-1">،</span>}
													</span>
												);
											})}
									</p>
								</div>
							)}

							{item.authors && item.authors.length > 0 && (
								<div className="flex items-center gap-1.5">
									<p className="font-light text-sm md:text-base whitespace-nowrap">
										نویسندگان
									</p>
									<p className="font-semibold text-sm md:text-lg">
										{item?.authors
											?.slice(0, 3)
											.map((author: any, index: number) => {
												const isLast =
													index === Math.min(item?.authors?.length ?? 0, 3) - 1;
												return (
													<span
														key={author.id ?? index}
														className="inline-flex items-center"
													>
														<Link
															href={`/characters/${author.id}`}
															className="hover:opacity-70 whitespace-nowrap"
														>
															{author.name}
														</Link>
														{!isLast && <span className="pl-1">،</span>}{" "}
													</span>
												);
											})}
									</p>
								</div>
							)}

							{item?.releaseYearFrom && (
								<div className="flex items-center gap-1.5">
									<p className="font-light text-sm md:text-base whitespace-nowrap">
										تاریخ شروع انتشار
									</p>
									<p className="font-semibold text-sm md:text-lg">
										{item?.releaseYearFrom}
									</p>
								</div>
							)}

							{item?.releaseYearTo && (
								<div className="flex items-center gap-1.5">
									<p className="font-light text-sm md:text-base whitespace-nowrap">
										تاریخ پایان انتشار
									</p>
									<p className="font-semibold text-sm md:text-lg">
										{item?.releaseYearTo}
									</p>
								</div>
							)}

							{item?.channels && item?.channels.length > 0 && (
								<div className="flex items-center gap-1.5">
									<p className="font-light text-sm md:text-base whitespace-nowrap">
										شبکه
									</p>
									<p className="font-semibold text-sm md:text-lg">
										{item?.channels
											?.slice(0, 3)
											.map((channel: any, index: number) => {
												const isLast =
													index ===
													Math.min(item?.channels?.length ?? 0, 3) - 1;
												return (
													<span
														key={channel.id ?? index}
														className="inline-flex items-center"
													>
														<Link
															href={`/series/channels/${channel.id}`}
															className="hover:opacity-70 whitespace-nowrap"
														>
															{channel.name}
														</Link>
														{!isLast && <span className="pl-1">،</span>}{" "}
													</span>
												);
											})}
									</p>
								</div>
							)}

							{item.actors && item.actors.length > 0 && (
								<div className="flex items-center gap-1.5 lg:col-span-2">
									<p className="font-light text-sm md:text-base whitespace-nowrap">
										بازیگران
									</p>
									<div className="flex flex-wrap font-semibold text-sm md:text-lg">
										{item?.actors
											?.slice(0, 3)
											.map((actor: any, index: number) => {
												const isLast =
													index === Math.min(item?.actors?.length ?? 0, 3) - 1;
												return (
													<Link
														key={actor.id ?? index}
														href={`/characters/${actor.id}`}
														className="hover:opacity-70 pl-1 whitespace-nowrap"
													>
														{actor.name}
														{!isLast && "،"}
													</Link>
												);
											})}
									</div>
								</div>
							)}
						</div>
						<div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
							<div className="flex items-center gap-1">
								<Image
									src="/images/scores/cutin.svg"
									alt="Cutin score"
									layout="responsive"
									objectFit="contain"
									width={90}
									height={41}
									className="max-w-[90px] max-h-[41px]"
								/>

								<span className="text-xs md:text-sm lg:text-lg font-semibold">
									{item.cutinScore?.toLocaleString() ?? 0}
								</span>
								<span className="text-xs md:text-sm lg:text-lg opacity-70">
									({item?.totalScore?.toLocaleString() ?? 0})
								</span>
							</div>

							{item.rottenTomatoes && item.rottenTomatoes.score && (
								<div className="flex items-center gap-4">
									<Image
										src="/images/scores/rt.svg"
										alt="Rotten Tomatoes score"
										layout="responsive"
										objectFit="contain"
										width={56}
										height={41}
										className="max-w-[28px] max-h-[20px] lg:max-w-[56px] lg:max-h-[41px]"
									/>
									<span className="text-xs md:text-sm lg:text-lg font-semibold">
										{item.rottenTomatoes.score}
									</span>
								</div>
							)}

							{item.metacritic && item.metacritic.score && (
								<div className="flex items-center gap-4">
									<Image
										src="/images/scores/metacritics.svg"
										alt="Meta critics score"
										layout="responsive"
										objectFit="contain"
										width={56}
										height={41}
										className="max-w-[28px] max-h-[20px] lg:max-w-[56px] lg:max-h-[41px]"
									/>
									<span className="text-xs md:text-sm lg:text-lg font-semibold">
										{item.metacritic.score}
									</span>
								</div>
							)}

							{item.imdb && item.imdb.score && (
								<div className="flex items-center gap-4">
									<Image
										src="/images/scores/imdb.svg"
										alt="IMDB score"
										layout="responsive"
										objectFit="contain"
										width={56}
										height={41}
										className="max-w-[28px] max-h-[20px] lg:max-w-[56px] lg:max-h-[41px]"
									/>
									<span className="text-xs md:text-sm lg:text-lg font-semibold">
										{item.imdb.score}
									</span>
								</div>
							)}
						</div>

						{item?.familyGuideList && item?.familyGuideList.length > 0 && (
							<Link
								className={`rounded-lg text-custom-pink bg-custom-pink/[0.15] py-[10px] md:py-[12px] px-[16px] md:px-[20px] w-max flex justify-center items-center gap-1 mb-2 text-[10px] md:text-[14px] lg:text-[18px]`}
								href={`/${kind?.kind}/${item.id}/user-manual`}
							>
								<FiExternalLink />
								راهنمای خانواده
							</Link>
						)}
						{item.tags && <Tags tags={item.tags} kind={kind} />}
						<div className="hidden xl:flex flex-wrap gap-2">
							{!detailedScore && (
								<div className="text-white rtl w-full" dir="rtl">
									<Rating
										contentId={item.id}
										disabled={loading || scored}
										kind={kind}
										setRating={setRating}
										rating={rating ?? 0}
										onSuccess={() => {
											setShowScore(true);
											setScored(true);
										}}
									/>
									<div className="mx-auto">
										<div className="flex items-center justify-between my-2">
											<div className="text-gray-300 text-sm">
												امتیار دهی به کل سریال بر اساس مجموعه امتیازات شما
												محاسبه میگردد و امتیاز کلی شما در نظر گرفته نمی‌شود.
											</div>
											{!loading && !scored && (
												<button
													className="flex items-center space-x-2 text-custom-pink px-4 py-2 rounded-lg gap-2"
													onClick={() => setDetailedScore(!detailedScore)}
												>
													<IoOpenOutline />
													<span>باز کردن امتیازدهی جزئی</span>
												</button>
											)}
											{rating !== undefined &&
												rating !== null &&
												!loading &&
												scored && (
													<button
														className="flex items-center space-x-2 text-red-600 px-4 py-2 rounded-lg gap-2"
														onClick={() => setRemoveScoreModal(true)}
													>
														<IoTrashBin />
														<span>حذف امتیاز کلی</span>
													</button>
												)}
										</div>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Poster */}
					<div className="w-full max-w-[376px] flex justify-start flex-col items-center lg:ml-20 gap-2 px-4">
						<div className={"w-full lg:w-[300px] xl:w-[376px] max-w-[400px]"}>
							<Image
								key={item?.id}
								src={
									process.env.NEXT_PUBLIC_FILE_URL + item?.backgroundImage?.code
								}
								alt={item?.title}
								width={376}
								height={526}
								className={` m-auto ${
									item?.cutinSuggested ? "rounded-t-lg" : "rounded-lg"
								}`}
							/>
							{item?.cutinSuggested && (
								<div className="w-full bg-light-blue/20 text-light-blue text-center py-2 rounded-b-lg text-[8px] md:text-[14px] lg:text-[16px]">
									پیشنهاد شده توسط تیم کاتین
								</div>
							)}
						</div>
						{/* Rating & Actions */}
						<div className="flex items-center w-full justify-between gap-2">
							<button
								disabled={marked}
								onClick={async () => {
									if (!user) {
										addToast("ابتدا وارد شوید", "info");
										return;
									}
									await callMarked(
										"/api/mark-as-done",
										{
											method: "POST",
											headers: { "Content-Type": "application/json" },
											body: JSON.stringify({
												contentId: item?.id,
											}),
										},
										true,
									);
									setMarked(true);
								}}
								className="rounded-lg text-[8px] md:text-[14px] lg:text-[18px] disabled:border-[1px] py-[6px] md:py-[7px] disabled:border-light-lime disabled:text-light-lime disabled:bg-light-lime/[0.12] bg-white/[.12] border-0 text-white w-full lg:w-[48%] hover:opacity-70 cursor-pointer"
							>
								تماشا کردم
							</button>
							<span className="rounded-lg text-center text-[8px] md:text-[14px] lg:text-[18px] py-[6px] md:py-[7px] w-full lg:w-[48%] text-white bg-white/[.12]">
								{getLabelFromEnumOptions(
									item?.status,
									MovieSeriesStatusOptions,
								)}
							</span>
						</div>
						{/* Watch Count */}
						<p className="rounded-lg text-center text-[8px] md:text-[14px] lg:text-[18px] py-[6px] md:py-[7px] w-full text-white bg-white/[.12]">
							{item?.markAsDoneCount?.toLocaleString() ?? 0} تماشا کردند
						</p>
					</div>
				</div>
				<div className="w-full">
					<div className="lg:hidden mx-auto px-5 flex flex-col items-start justify-start pb-4 gap-2">
						{item.seasons && item.seasons.length > 0 && (
							<div className="flex items-center gap-1.5">
								<p className="font-light text-sm md:text-base whitespace-nowrap">
									تعداد فصل
								</p>
								<p className="font-semibold text-sm md:text-lg">
									{item.seasons?.length}
								</p>
							</div>
						)}
						<div className="flex items-center gap-1.5">
							<p className="font-light text-sm md:text-base whitespace-nowrap">
								تعداد جوایز
							</p>
							<p className="font-semibold text-sm md:text-lg">
								{item.awards?.length ?? 0}
							</p>
						</div>
						{item.duration && (
							<div className="flex items-center gap-1.5">
								<p className="font-light text-sm md:text-base">
									مدت زمان هر قسمت
								</p>
								<p className="font-semibold text-sm md:text-lg">
									{minutesToPersian(item?.duration)}
								</p>
							</div>
						)}

						{item.genres && item.genres.length > 0 && (
							<div className="flex items-center gap-1.5">
								<p className="font-light text-sm md:text-base whitespace-nowrap">
									ژانرها
								</p>
								<div className="flex flex-wrap font-semibold text-sm md:text-lg">
									{item?.genres.slice(0, 3).map((genre: any, index: number) => (
										<Link
											href={`/${kind?.kind}/genres/${genre.id}`}
											key={index}
											className="hover:opacity-70 pl-1 whitespace-nowrap"
										>
											{genre.persianName}
											{index !== Math.min(item?.genres.length - 1, 2) && "،"}
										</Link>
									))}
								</div>
							</div>
						)}

						{item.directors && item.directors.length > 0 && (
							<div className="flex items-center gap-1.5">
								<p className="font-light text-sm md:text-base whitespace-nowrap">
									کارگردان
								</p>
								<p className="font-semibold text-sm md:text-lg">
									{item?.directors
										?.slice(0, 3)
										.map((director: any, index: number) => {
											const isLast =
												index === Math.min(item?.directors?.length ?? 0, 3) - 1;
											return (
												<span
													key={director.id ?? index}
													className="inline-flex items-center"
												>
													<Link
														href={`/characters/${director.id}`}
														className="hover:opacity-70 whitespace-nowrap"
													>
														{director.name}
													</Link>
													{!isLast && <span className="pl-1">،</span>}
												</span>
											);
										})}
								</p>
							</div>
						)}

						{item.authors && item.authors.length > 0 && (
							<div className="flex items-center gap-1.5">
								<p className="font-light text-sm md:text-base whitespace-nowrap">
									نویسندگان
								</p>
								<p className="font-semibold text-sm md:text-lg">
									{item?.authors
										?.slice(0, 3)
										.map((author: any, index: number) => {
											const isLast =
												index === Math.min(item?.authors?.length ?? 0, 3) - 1;
											return (
												<span
													key={author.id ?? index}
													className="inline-flex items-center"
												>
													<Link
														href={`/characters/${author.id}`}
														className="hover:opacity-70 whitespace-nowrap"
													>
														{author.name}
													</Link>
													{!isLast && <span className="pl-1">،</span>}{" "}
												</span>
											);
										})}
								</p>
							</div>
						)}

						{item?.releaseYearFrom && (
							<div className="flex items-center gap-1.5">
								<p className="font-light text-sm md:text-base whitespace-nowrap">
									تاریخ شروع انتشار
								</p>
								<p className="font-semibold text-sm md:text-lg">
									{item?.releaseYearFrom}
								</p>
							</div>
						)}

						{item?.releaseYearTo && (
							<div className="flex items-center gap-1.5">
								<p className="font-light text-sm md:text-base whitespace-nowrap">
									تاریخ پایان انتشار
								</p>
								<p className="font-semibold text-sm md:text-lg">
									{item?.releaseYearTo}
								</p>
							</div>
						)}

						{item?.channels && item?.channels.length > 0 && (
							<div className="flex items-center gap-1.5">
								<p className="font-light text-sm md:text-base whitespace-nowrap">
									شبکه
								</p>
								<p className="font-semibold text-sm md:text-lg">
									{item?.channels
										?.slice(0, 3)
										.map((channel: any, index: number) => {
											const isLast =
												index === Math.min(item?.channels?.length ?? 0, 3) - 1;
											return (
												<span
													key={channel.id ?? index}
													className="inline-flex items-center"
												>
													<Link
														href={`/series/channels/${channel.id}`}
														className="hover:opacity-70 whitespace-nowrap"
													>
														{channel.name}
													</Link>
													{!isLast && <span className="pl-1">،</span>}{" "}
												</span>
											);
										})}
								</p>
							</div>
						)}

						{item.actors && item.actors.length > 0 && (
							<div className="flex items-center gap-1.5 lg:col-span-2">
								<p className="font-light text-sm md:text-base whitespace-nowrap">
									بازیگران
								</p>
								<div className="flex flex-wrap font-semibold text-sm md:text-lg">
									{item?.actors
										?.slice(0, 3)
										.map((actor: any, index: number) => {
											const isLast =
												index === Math.min(item?.actors?.length ?? 0, 3) - 1;
											return (
												<Link
													key={actor.id ?? index}
													href={`/characters/${actor.id}`}
													className="hover:opacity-70 pl-1 whitespace-nowrap"
												>
													{actor.name}
													{!isLast && "،"}
												</Link>
											);
										})}
								</div>
							</div>
						)}
					</div>
					{!detailedScore && (
						<div className="xl:hidden px-6">
							<div className="text-white rtl w-full" dir="rtl">
								<Rating
									disabled={loading || scored}
									contentId={item.id}
									kind={kind}
									setRating={setRating}
									rating={rating ?? 0}
									onSuccess={() => {
										setShowScore(true);
										setScored(true);
									}}
								/>
								<div className="mx-auto">
									<div className="flex items-center justify-between mb-6">
										<div className="text-gray-300 text-sm">
											امتیار دهی به کل فیلم بر اساس مجموعه امتیازات شما محاسبه
											میگردد و امتیاز کلی شما در نظر گرفته نمی‌شود.
										</div>
										{!loading && !scored && (
											<button
												className="flex items-center space-x-2 text-custom-pink px-4 py-2 rounded-lg gap-2 text-xs md:text-sm"
												onClick={() => setDetailedScore(!detailedScore)}
											>
												<IoOpenOutline />
												<span>باز کردن امتیازدهی جزئی</span>
											</button>
										)}
										{rating !== undefined &&
											rating !== null &&
											!loading &&
											scored && (
												<button
													className="flex items-center space-x-2 text-red-600 px-4 py-2 rounded-lg gap-2 text-xs md:text-sm"
													onClick={() => setRemoveScoreModal(true)}
												>
													<IoTrashBin />
													<span>حذف امتیاز کلی</span>
												</button>
											)}
									</div>
								</div>
							</div>
						</div>
					)}
					{/* Rating Section */}
					{detailedScore && !rating && (
						<div className="text-white p-4 lg:p-6 rtl w-full" dir="rtl">
							<div className="mx-auto">
								<div className="flex items-center justify-between mb-6">
									<div className="text-gray-300 text-sm">
										امتیار دهی به کل سریال بر اساس مجموعه امتیازات شما محاسبه
										میگردد و امتیاز کلی شما در نظر گرفته نمی‌شود.
									</div>
									{!episodeScores ||
										(episodeScores.length === 0 && (
											<button
												className="flex items-center space-x-2 text-custom-pink px-4 py-2 rounded-lg gap-2 text-xs md:text-sm"
												onClick={() => setDetailedScore(!detailedScore)}
											>
												<BsFillXSquareFill />
												<span>بستن امتیازدهی</span>
											</button>
										))}
									{episodeScores && episodeScores.length > 0 && (
										<button
											className="flex items-center space-x-2 text-custom-pink px-4 py-2 rounded-lg gap-2 text-xs md:text-sm"
											onClick={() => setRemoveScoreModal(true)}
										>
											<BsFillXSquareFill />
											<span>حذف امتیازدهی جزئی</span>
										</button>
									)}
								</div>
								<RatingSeries
									episodeScores={episodeScores}
									disabled={loading}
									refetch={() => {
										mutate();
										fetchUserScores();
									}}
									chapters={item.seasons}
									contentId={item.id}
									setShowScore={setShowScore}
									setScoreSeason={setScoreSeason}
								/>
							</div>
						</div>
					)}
					{secondRowAds.length > 0 && <TwoColumnsAds ads={secondRowAds} />}
					{clips &&
						((clips.news && clips.news.length > 0) || clips.trailer) && (
							<div className={"green-grid-radial w-full px-6"}>
								<div className={"my-[20px] relative z-10"}>
									<h2 className="text-white text-xl font-bold my-[28px] text-right md:text-[32px]">
										آخرین فیلم ها
									</h2>
									<RecentClips
										lastClips={clips.news}
										showKinds={false}
										trailer={trailer}
									/>
								</div>
							</div>
						)}
					{thirdRowAds.length > 0 && <ThreeColumnsAds ads={thirdRowAds} />}

					<div className="px-6">
						{item?.awards && item.awards.length > 0 && (
							<ItemAwards awards={item.awards} />
						)}
						<NewsContentSlider
							news={news}
							newsActiveColor={newsActiveColor}
							setNewsActiveColor={setNewsActiveColor}
						/>
						<div className={"w-full py-4"}>
							<Comments
								comments={item?.comments ?? []}
								refresh={mutate}
								user={user}
								itemId={item?.id}
								type="CONTENT"
							/>
						</div>
					</div>
				</div>
			</div>
			{showScore && (
				<ShowScore
					score={rating ?? 0}
					image={process.env.NEXT_PUBLIC_FILE_URL + item?.backgroundImage?.code}
					title={item?.title}
					username={user?.username ?? ""}
					avatar={null}
					onClose={() => setShowScore(false)}
					scoreSeason={scoreSeason}
				/>
			)}
			{removeScoreModal && (
				<RemoveScoreModal
					onClose={() => {
						setRemoveScoreModal(false);
					}}
					onSuccess={() => {
						setRemoveScoreModal(false);
						mutate();
						setRating(0);
						setScored(false);
						setEpisodeScores([]);
					}}
					isEpisode={detailedScore}
					contentId={item.id}
				/>
			)}
		</>
	);
}
